import { useCallback, useEffect, useRef, useState } from "react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";

/**
 * Searchable, infinite-scrolling async Autocomplete.
 *
 * Fetches options one page at a time (server-side search + pagination), so large
 * lists load 50 at a time and grow as the user scrolls to the bottom of the list
 * instead of loading everything (or just the first page) up front.
 *
 * The value is controlled by id: pass the selected id via `value` and receive
 * `onChange(id, option)` back. Clearing the field yields `onChange("", null)`.
 *
 * Props:
 * - fetchPage(page, search) => Promise<{ items: object[], hasMore: boolean }>
 * - selectedOption : full object for the current value (lets edit forms show the
 *   label before options have loaded)
 * - refreshKey     : when it changes, the loaded options are reset (e.g. cities
 *   depend on the selected zone)
 */
export default function AsyncAutocomplete({
  label,
  value = "",
  onChange,
  fetchPage,
  getOptionLabel = (o) => o?.name ?? "",
  renderOption,
  selectedOption = null,
  refreshKey = null,
  disabled = false,
  error = false,
  helperText,
  size = "small",
  fullWidth = false,
  sx,
  placeholder,
  noOptionsText,
}) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [valueObj, setValueObj] = useState(selectedOption);

  const searchRef = useRef("");
  const debounceRef = useRef(null);
  const reqIdRef = useRef(0);

  // Resolve the displayed value object from `value` + loaded options / selectedOption.
  useEffect(() => {
    if (value === "" || value === null || value === undefined) {
      setValueObj(null);
      return;
    }
    const match = options.find((o) => String(o.id) === String(value));
    if (match) {
      setValueObj(match);
    } else if (selectedOption && String(selectedOption.id) === String(value)) {
      setValueObj(selectedOption);
    }
  }, [value, options, selectedOption]);

  const load = useCallback(
    async (pageToLoad, search, replace) => {
      const reqId = ++reqIdRef.current;
      setLoading(true);
      try {
        const { items = [], hasMore: more = false } = await fetchPage(
          pageToLoad,
          search,
        );
        if (reqId !== reqIdRef.current) return; // a newer request superseded this one
        setOptions((prev) => {
          const base = replace ? [] : prev;
          const byId = new Map(base.map((o) => [o.id, o]));
          items.forEach((it) => byId.set(it.id, it));
          return Array.from(byId.values());
        });
        setHasMore(more);
        setPage(pageToLoad);
      } catch (e) {
        if (reqId === reqIdRef.current) setHasMore(false);
      } finally {
        if (reqId === reqIdRef.current) setLoading(false);
      }
    },
    [fetchPage],
  );

  // Reset loaded options whenever the refresh key changes (e.g. zone changed).
  useEffect(() => {
    setOptions([]);
    setPage(1);
    setHasMore(false);
    setLoaded(false);
    searchRef.current = "";
  }, [refreshKey]);

  // Lazily load the first page the first time the dropdown is opened.
  useEffect(() => {
    if (open && !loaded) {
      setLoaded(true);
      load(1, "", true);
    }
  }, [open, loaded, load]);

  const handleInputChange = (event, newInput, reason) => {
    if (reason !== "input") return;
    searchRef.current = newInput;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      load(1, newInput, true);
    }, 400);
  };

  const handleScroll = (event) => {
    const el = event.currentTarget;
    if (
      hasMore &&
      !loading &&
      el.scrollTop + el.clientHeight >= el.scrollHeight - 48
    ) {
      load(page + 1, searchRef.current, false);
    }
  };

  // Ensure the selected value is always renderable even if it isn't in the
  // currently-loaded page (avoids MUI's "value not in options" warning).
  const displayOptions =
    valueObj && !options.some((o) => String(o.id) === String(valueObj.id))
      ? [valueObj, ...options]
      : options;

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      disabled={disabled}
      fullWidth={fullWidth}
      size={size}
      sx={sx}
      options={displayOptions}
      loading={loading}
      value={valueObj}
      getOptionLabel={getOptionLabel}
      renderOption={renderOption}
      isOptionEqualToValue={(option, val) => String(option.id) === String(val?.id)}
      filterOptions={(x) => x}
      noOptionsText={noOptionsText}
      onChange={(e, newValue) => {
        setValueObj(newValue);
        onChange?.(newValue ? newValue.id : "", newValue);
      }}
      onInputChange={handleInputChange}
      slotProps={{ listbox: { onScroll: handleScroll } }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
